package dev.lingvo.site.ds

import androidx.compose.runtime.Composable
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.alignItems
import com.varabyte.kobweb.compose.ui.modifiers.attr
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.flexWrap
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.modifiers.margin
import com.varabyte.kobweb.compose.ui.modifiers.width
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.base
import com.varabyte.kobweb.silk.style.breakpoint.Breakpoint
import com.varabyte.kobweb.silk.style.cssRule
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import dev.lingvo.site.i18n.t
import dev.lingvo.site.i18n.tf
import kotlinx.browser.document
import org.jetbrains.compose.web.css.AlignItems
import org.jetbrains.compose.web.css.FlexWrap
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Section
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text
import org.w3c.dom.HTMLElement

class LFilterField(
    val id: String,
    val label: String,
    val activeValue: String? = null,
    val onClear: (() -> Unit)? = null,
    val control: @Composable (Modifier) -> Unit,
)

val LFilterBarStyle = CssStyle {
    base {
        Modifier
            .width(100.percent)
            .gap(Space.SM)
            .color(colorMode.toSitePalette().textPrimary)
            .styleModifier { property("display", "flex"); property("flex-direction", "column") }
    }
    /**
     * A wrapped filter bar has one column, not four ragged ones.
     *
     * The control row wraps, and every control in it sizes itself to its own content, so at 390 the
     * keys page drew a full-width search box above a 240px file picker above a 200px language picker
     * above a 180px state picker above a 200px tag field — four different right edges under one left
     * edge, reading as a layout that had come apart rather than as a set of filters. The controls
     * are not different sizes because anybody decided they should be; they are different sizes
     * because "All files" is a longer word than "All".
     *
     * So below MD each control takes a whole line: `flex-basis: 100%` puts one per row and
     * `flex-grow: 1` makes each fill it. Above MD the row is wide enough to hold them side by side
     * and intrinsic widths are right again, which is what the second rule restores.
     *
     * Written as rules on this already-registered style rather than as a style of its own, for the
     * reason `PageHeaderStyle` gives: a new top-level style has to be added by name to every test
     * harness that renders one of these. The selector reaches through the per-field wrapper, which
     * is `display: contents` and therefore not itself the flex item.
     */
    cssRule(" [data-filter-bar-fields] > * > *") {
        Modifier.styleModifier { property("flex", "1 1 100%") }
    }
    cssRule(Breakpoint.MD, " [data-filter-bar-fields] > * > *") {
        Modifier.styleModifier { property("flex", "0 1 auto") }
    }
}

val LFilterChipRowStyle = CssStyle.base {
    Modifier
        .fillMaxWidth()
        .gap(Space.SM)
        .alignItems(AlignItems.Center)
        .flexWrap(FlexWrap.Wrap)
        .styleModifier { property("display", "flex") }
}

/**
 * @param resultSummary the honest count under the controls — "48 of 1,204". Announced politely,
 *   because it changes as a consequence of what the reader just typed and is the answer to it.
 * @param actions the one thing the current filter makes possible, at the inline end of the chip
 *   row: "Pre-translate the 412 untranslated in German" belongs beside the filter that scoped it,
 *   not in a header three sections away that says nothing about German.
 */
@Composable
fun LFilterBar(
    fields: List<LFilterField>,
    onClearAll: () -> Unit,
    modifier: Modifier = Modifier,
    label: String = t("ds.ds.lfilter_bar.filters", "Filters"),
    resultSummary: String? = null,
    actions: (@Composable () -> Unit)? = null,
) {
    Section(
        LFilterBarStyle.toModifier().then(modifier).toAttrs {
            attr("role", "search")
            attr("aria-label", label)
        },
    ) {
        Div(
            Modifier
                .fillMaxWidth()
                .gap(Space.SM)
                .flexWrap(FlexWrap.Wrap)
                .alignItems(AlignItems.FlexEnd)
                .styleModifier { property("display", "flex") }
                // Named so `LFilterBarStyle` can give every control in it one width when it wraps.
                .toAttrs { attr("data-filter-bar-fields", "") },
        ) {
            fields.forEach { field ->
                Div(
                    Modifier
                        .styleModifier { property("display", "contents") }
                        .attr("id", field.id)
                        .toAttrs(),
                ) {
                    field.control(Modifier)
                }
            }
        }

        val active = fields.filter { it.activeValue != null }
        // **The second row is not only the chips.** It also carries the result count and the
        // filter-scoped action, and both of those are worth drawing on a bar with nothing active —
        // "1,204 keys" is the answer to a question the reader has before they touch a control. It
        // used to render only when a chip existed, so an unfiltered table showed no total at all.
        if (active.isNotEmpty() || resultSummary != null || actions != null) {
            Div(LFilterChipRowStyle.toModifier().toAttrs()) {
                active.forEach { field ->
                    LChip(
                        text = "${field.label}: ${field.activeValue}",
                        onRemove = field.onClear?.let { clear ->
                            { clear(); focusFirstControlIn(field.id) }
                        },
                        removeLabel = tf(
                            "ds.ds.lfilter_bar.clear_the_label_filter",
                            "Clear the {label} filter",
                            "label" to field.label.lowercase(),
                        ),
                    )
                }
                if (active.isNotEmpty()) {
                    LButton(
                        label = t("ds.ds.lfilter_bar.clear_filters", "Clear filters"),
                        onClick = {
                            onClearAll()
                            fields.firstOrNull()?.let { focusFirstControlIn(it.id) }
                        },
                        variant = LButtonVariant.GHOST,
                        size = LButtonSize.SM,
                    )
                }
                Div(
                    Modifier
                        .gap(Space.SM)
                        .alignItems(AlignItems.Center)
                        .flexWrap(FlexWrap.Wrap)
                        .styleModifier {
                            property("display", "flex")
                            property("margin-inline-start", "auto")
                        }
                        .toAttrs { attr("data-filter-bar-trailing", "") },
                ) {
                    if (resultSummary != null) {
                        Span(
                            Modifier
                                .color(ColorMode.current.toSitePalette().textSecondary)
                                .fontSize(TypeScale.Small)
                                .tabularNumbers()
                                .toAttrs { attr("aria-live", "polite") },
                        ) {
                            Text(resultSummary)
                        }
                    }
                    actions?.invoke()
                }
            }
        }
    }
}

fun List<LFilterField>.narrowestActiveLabel(): String? = lastOrNull { it.activeValue != null }?.label

/**
 * Puts focus on the first thing inside [wrapperId] that can take it.
 *
 * Shared with [LBulkActionBar], which has the same problem in a different place: a control that
 * removes itself when pressed leaves focus on `<body>`, and the reader's next Tab starts again from
 * the top of the document. Both need to hand focus back to the control the selection or the filter
 * came from, and neither knows which element inside it is focusable.
 */
internal fun focusFirstControlIn(wrapperId: String) {
    val wrapper = document.getElementById(wrapperId) as? HTMLElement ?: return
    (wrapper.querySelector(
        "button:not(:disabled), input:not(:disabled), textarea:not(:disabled), " +
            "select:not(:disabled), a[href], [tabindex]:not([tabindex='-1'])",
    ) as? HTMLElement)?.focus()
}
